import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import Label from "app/components/Programs/Label";
import SelectBox from "app/components/OptionButtonGroup/SelectBox";
import StateDayPicker from "app/components/Programs/StateDayPicker";
import SwitchToggle from "app/components/OptionButtonGroup/SwitchToggle";
import { bill_start_options } from "../utils";
import styles from "./styles.module.scss";

export type BillStartOption = { value: string; label: string };

// CN-2832: what each start option does to the first charge, shown under the
// select so a coach sees the proration rule before choosing.
const BILL_START_HINTS: Record<string, string> = {
  today: "Charged at checkout, then on the same day each billing cycle.",
  first_of_month: "First charge is prorated to the next 1st, then full price on the 1st.",
  fifteenth_of_month: "First charge is prorated to the next 15th, then full price on the 15th.",
  custom_date:
    "Nobody is charged at signup. Everyone who buys before this date pays the full price on it, then on the same day each billing cycle.",
};

export const billStartOptionFor = (value: string | null | undefined): BillStartOption =>
  bill_start_options.find((option) => option.value === value) || bill_start_options[0];

// The fields both the create form and the edit modal send for CN-2832.
export const subscriptionStartPayload = (values: {
  bill_start: BillStartOption;
  billing_start_on?: string | null;
  carry_over_sessions?: boolean;
}) => ({
  bill_start: values.bill_start.value,
  billing_start_on: values.bill_start.value === "custom_date" ? values.billing_start_on : null,
  carry_over_sessions: !!values.carry_over_sessions,
});

// CN-2832: Stripe takes the start date only inside the first billing period,
// so the latest date is one billing period from today. A form without the
// billing period (the edit modal) uses one month, the shortest one.
export const latestCustomStartDate = (billingMonths: unknown): DateTime =>
  DateTime.local().startOf("day").plus({ months: Number(billingMonths) || 1 });

// CN-2832: the custom date must be a day in the future, at most one billing
// period away. Shared with the forms that submit without react-hook-form's
// own validation.
export const customStartDateError = (
  billStart: BillStartOption | undefined,
  billingStartOn: string | null | undefined,
  t: (key: string) => string,
  billingMonths?: unknown,
): string | null => {
  if (billStart?.value !== "custom_date") {
    return null;
  }
  if (!billingStartOn) {
    return t("Choose the date of the first charge");
  }
  const startsOn = DateTime.fromISO(billingStartOn).startOf("day");
  if (startsOn <= DateTime.local().startOf("day")) {
    return t("The date must be in the future");
  }
  return startsOn > latestCustomStartDate(billingMonths)
    ? t("The date must be within one billing period from today")
    : null;
};

type PropsDef = {
  // The edit modal says edits only reach new signups; the create form doesn't.
  appliesToNewSignupsOnly?: boolean;
};

export default function SubscriptionStartFields({ appliesToNewSignupsOnly = false }: PropsDef) {
  const { t } = useTranslation();
  const { control, watch, formState } = useFormContext();
  const billStart: BillStartOption = watch("bill_start");
  const isCustomDate = billStart?.value === "custom_date";
  const tomorrow = DateTime.local().plus({ days: 1 }).startOf("day").toJSDate();
  const billingMonths = watch("pricing_time");
  const dateError = (formState.errors as any)?.billing_start_on?.message;

  return (
    <div className={styles.container}>
      <Label
        text={t("Billing Start Date")}
        item={(
          <Controller
            name="bill_start"
            control={control}
            render={({ field }) => (
              <SelectBox
                options={bill_start_options.map((option) => ({ value: option.value, label: t(option.label) }))}
                value={{ value: field.value?.value, label: t(field.value?.label ?? "") }}
                setValue={field.onChange}
                customWidth="100%"
                customControl={{ borderRadius: "8px" }}
              />
            )}
          />
        )}
      />
      {billStart?.value && (
        <p className={styles.hint}>{t(BILL_START_HINTS[billStart.value])}</p>
      )}

      {isCustomDate && (
        <Label
          text={t("First Charge On")}
          required={true}
          error={dateError}
          item={(
            <Controller
              name="billing_start_on"
              control={control}
              rules={{ validate: (value) => customStartDateError(billStart, value, t, billingMonths) ?? true }}
              render={({ field }) => (
                <div data-testid="billing-start-on">
                  <StateDayPicker
                    date={field.value ? DateTime.fromISO(field.value).toJSDate() : null}
                    openToDate={field.value ? null : tomorrow}
                    minDate={tomorrow}
                    maxDate={latestCustomStartDate(billingMonths).toJSDate()}
                    onChange={(value: string) =>
                      field.onChange(value ? DateTime.fromISO(value).toISODate() : null)
                    }
                  />
                </div>
              )}
            />
          )}
        />
      )}

      <Controller
        name="carry_over_sessions"
        control={control}
        render={({ field }) => (
          <div className={styles.carry_over}>
            <SwitchToggle
              small
              checked={!!field.value}
              onChange={field.onChange}
              title={t("Carry over unused sessions to the next billing cycle")}
              data-testid="carry-over-sessions-toggle"
            />
            <p className={styles.hint}>
              {t(
                "Unused sessions add to next cycle's sessions instead of expiring. Everything left expires when the subscription ends."
              )}
            </p>
          </div>
        )}
      />

      {appliesToNewSignupsOnly && (
        <p className={styles.hint}>
          {t("Changes to the start date and carry-over apply to new subscribers only.")}
        </p>
      )}
    </div>
  );
}
